import { Component, HostListener, NgZone, OnInit, inject } from '@angular/core';
import { Router } from '@angular/router';
import { VwApiRequestsService } from 'vwappservices';
import { CommonServicesService } from './services/common-services.service';
import { initTableHeightFitting } from './shared/utils/fit-table-height.util';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false
})
export class AppComponent implements OnInit {

  @HostListener('window:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    if (event.key === "F4") {
      this.common.isActive = !this.common.isActive;
    }
  }

  // ---- App-wide: entering a text box by mouse selects its whole value, like Tab does ------
  // Applies to every screen (current and future) with no per-field markup. Tab-focus already
  // selects the contents; a click only drops the caret. On a mouse-initiated focus we select
  // everything, then swallow that click's mouseup (which would otherwise collapse the selection
  // back to a caret). A further click inside an already-focused box isn't a focus, so it still
  // places the caret for editing part of a value.
  // Skipped: textareas, read-only/disabled inputs, non-text types (checkbox, date, password...).
  // Opt a field out with the attribute `data-no-autoselect`.
  private static readonly AUTOSELECT_TYPES = ['text', 'number', 'tel', 'search', 'email', 'url', ''];
  private mouseIsDown = false;
  private autoSelectPendingMouseUp = false;

  @HostListener('document:mousedown')
  onDocumentMouseDown() {
    this.mouseIsDown = true;
  }

  @HostListener('document:focusin', ['$event'])
  onDocumentFocusIn(event: FocusEvent) {
    const el = event.target;
    if (!(el instanceof HTMLInputElement) || el.readOnly || el.disabled) return;
    if (!AppComponent.AUTOSELECT_TYPES.includes(el.type) || el.hasAttribute('data-no-autoselect')) return;
    el.select();
    // Only a mouse-initiated focus has a mouseup to swallow; after Tab, the next click in the
    // same box must place the caret as usual.
    this.autoSelectPendingMouseUp = this.mouseIsDown;
  }

  @HostListener('document:mouseup', ['$event'])
  onDocumentMouseUp(event: MouseEvent) {
    this.mouseIsDown = false;
    if (!this.autoSelectPendingMouseUp) return;
    this.autoSelectPendingMouseUp = false;
    if (event.target instanceof HTMLInputElement) event.preventDefault();
  }

  private vwApi = inject(VwApiRequestsService);
  private router = inject(Router);
  private ngZone = inject(NgZone);

  constructor(private common: CommonServicesService) {}

  ngOnInit(): void {
    this.vwApi.setUnauthorisedHandler(() => this.router.navigateByUrl('/login'));
    // Outside the zone: it only sets table styles, and its body-wide MutationObserver would
    // otherwise run a full change detection on every DOM change — which loops forever with any
    // view that re-renders DOM on each check (e.g. the "?" popovers on Create Purchase).
    this.ngZone.runOutsideAngular(() => initTableHeightFitting());
  }
}
